import {RightEditorDrawerTabState, EditorDrawerTabState} from "@buildwithflux/constants";
import {DocumentUid, UserUid, UserUiState, UserUiStateMap} from "@buildwithflux/models";
import {UserUiStateRepository} from "@buildwithflux/repositories";
import {areWeTestingWithJest, isDevEnv, Logger} from "@buildwithflux/shared";
import {create as createMutative} from "mutative";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";

import {useFluxServices} from "injection/hooks";
import {CurrentUserService} from "modules/auth";
import {drawerTabConfigurationService} from "modules/drawer_tabs/drawerTabConfigurationService";
import {FeatureFlagsState, FeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";

export type Uninitialized = {
    userUiStates: undefined;
    type: "uninitialized";
};

export type Initialized = {
    userUiStates: UserUiStateMap;
    type: "initialized";
};

type UserUiStateStoreState = Uninitialized | Initialized;
type UserUiStateStoreApi = {
    loadCurrentUserUiState(documentUid: DocumentUid): Promise<void>;

    setRightHandPanelTabState(documentUid: DocumentUid, tabState: RightEditorDrawerTabState): void;

    setEditorDrawerTabState(documentUid: DocumentUid, tabState: EditorDrawerTabState): void;

    clear(): void;
};

type UserUiStore = UserUiStateStoreState & UserUiStateStoreApi;
export type UserUiStateStore = StoreApi<UserUiStore>;
export type UseUserUiStateStore = UseBoundStore<UserUiStateStore>;

export const createUserUiStateStore = (
    currentUserService: CurrentUserService,
    userUiStateRepository: UserUiStateRepository,
    featureFlagsStore: FeatureFlagsStore,
    logger: Logger,
): UseUserUiStateStore => {
    return create<UserUiStore>()(
        devtools(
            (set, _get) => {
                return {
                    type: "uninitialized",
                    userUiStates: undefined,
                    loadCurrentUserUiState: async (documentUid: DocumentUid) => {
                        const currentUser = currentUserService.getCurrentUser();

                        if (!currentUser) return;

                        // If not remembering panel config, no need to load anything, return initialized as copilotChat.
                        if (!shouldRememberPanelConfig(featureFlagsStore.getState())) {
                            set(
                                createMutative((state: UserUiStore) => {
                                    if (state.type === "uninitialized") {
                                        Object.assign(state, {
                                            type: "initialized",
                                            userUiStates: {
                                                [currentUser.uid]: RightEditorDrawerTabState.copilotChat,
                                            },
                                        });
                                    }
                                }),
                            );
                        }

                        const currentUserUiState = await userUiStateRepository.getUiState(documentUid, currentUser.uid);

                        set(
                            createMutative((state: UserUiStore) => {
                                if (state.type === "uninitialized") {
                                    Object.assign(state, {
                                        type: "initialized",
                                        userUiStates: {
                                            [currentUser.uid]: currentUserUiState
                                                ? currentUserUiState
                                                : RightEditorDrawerTabState.copilotChat,
                                        },
                                    });
                                }
                            }),
                        );
                    },
                    setRightHandPanelTabState: async (
                        documentUid: DocumentUid,
                        tabState: RightEditorDrawerTabState,
                    ) => {
                        const currentUser = currentUserService.getCurrentUser();

                        if (!currentUser) return;

                        const uiState: UserUiState = {
                            userUid: currentUser.uid,
                            rightHandPanelState: {
                                tabState,
                            },
                        };

                        set(
                            createMutative((state: UserUiStore) => {
                                if (state.type !== "initialized") {
                                    throw new Error("UseUserUiStateStore has been not initialized yet");
                                }
                                state.userUiStates[currentUser.uid] = uiState;
                            }),
                        );

                        if (shouldRememberPanelConfig(featureFlagsStore.getState())) {
                            await userUiStateRepository
                                .setUiState(uiState, documentUid, currentUser.uid)
                                .catch((error) => {
                                    logger.error("Error when saving user selection for right-hand panel", error);
                                });
                        }
                    },
                    setEditorDrawerTabState: async (documentUid: DocumentUid, tabState: EditorDrawerTabState) => {
                        const targetDrawer = drawerTabConfigurationService.getDrawerForTab(tabState);

                        if (targetDrawer === "right") {
                            const currentUser = currentUserService.getCurrentUser();

                            if (!currentUser) return;

                            const uiState: UserUiState = {
                                userUid: currentUser.uid,
                                rightHandPanelState: {
                                    tabState: tabState as RightEditorDrawerTabState,
                                },
                            };

                            set(
                                createMutative((state: UserUiStore) => {
                                    if (state.type !== "initialized") {
                                        throw new Error("UseUserUiStateStore has been not initialized yet");
                                    }
                                    state.userUiStates[currentUser.uid] = uiState;
                                }),
                            );

                            if (shouldRememberPanelConfig(featureFlagsStore.getState())) {
                                await userUiStateRepository
                                    .setUiState(uiState, documentUid, currentUser.uid)
                                    .catch((error) => {
                                        logger.error("Error when saving user selection for right-hand panel", error);
                                    });
                            }
                        }
                    },
                    clear: () => {
                        set({
                            type: "uninitialized",
                            userUiStates: undefined,
                        });
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "UserUiStore"},
        ),
    );
};

/**
 * Check the feature flag value to check if we should remember the selection
 * state in right hand panel:
 * 1. "off" and "control": don't remember the user's state, always open to copilot chat
 * 2. "on" and "remember": first time a user opens a particular document, open to the copilot chat, after that remember
 * which tab they're on last and open to that tab.
 */
const shouldRememberPanelConfig = (state: FeatureFlagsState) => {
    const rememberPanelConfig = state.segmentRememberPanelConfig;
    return rememberPanelConfig === "on" || rememberPanelConfig === "remember";
};

const validRightEditorDrawerTabStates = new Set(Object.values(RightEditorDrawerTabState));

/**
 * Gets the last tab selection in the right hand panel
 */
export function useRightHandPanelTabSelectionForCurrentUser(currentUserUid: UserUid | undefined) {
    return useFluxServices().useUserUiStateStore((store) => {
        if (!currentUserUid || store.type === "uninitialized") {
            return RightEditorDrawerTabState.copilotChat;
        }

        const userUiState = store.userUiStates[currentUserUid];

        // Fallback to copilot chat, this can happen when first time a user opens a particular document
        if (!userUiState || !userUiState.rightHandPanelState) {
            return RightEditorDrawerTabState.copilotChat;
        }

        const persistedTabState = userUiState.rightHandPanelState.tabState;

        // Fallback to copilot chat if the persisted value is not a valid tab state
        // This also handles migration of legacy "projectChat" values for historical documents
        if (!validRightEditorDrawerTabStates.has(persistedTabState)) {
            return RightEditorDrawerTabState.copilotChat;
        }

        // Always use the persisted selection
        return persistedTabState;
    });
}

export function useIsRightHandPanelInitialized() {
    return useFluxServices().useUserUiStateStore((store) => store.type === "initialized");
}
